'use client';
import { useEffect } from 'react';
import { reportCrash } from '@/lib/crash';

// Patterns that indicate third-party script noise, not our app crashes
const THIRD_PARTY_PATTERNS = [
  /chrome-extension:\/\//,
  /cortex-api\./,
  /livechat\.js/,
];

// Benign browser notifications that fire as errors but don't break the page
const BENIGN_MESSAGES = [
  /ResizeObserver loop/,
  // Thrown by browser extensions' injected RPC/messaging bridges (e.g.
  // Grammarly, ad blockers, video-download helpers) — no stack trace is
  // attached, so it isn't caught by isThirdParty, and it's never actionable.
  /Object Not Found Matching Id/,
];

// Browsers strip filename/line/col/stack for cross-origin script errors (spec
// behavior, not a bug) and replace the message with exactly this string — no
// debuggable info is ever available, so it's never actionable.
const CROSS_ORIGIN_MESSAGE = /^Script error\.?$/;

// Errors from unresolvable sources (inline/eval scripts) we can't act on
const UNATTRIBUTABLE_MESSAGES = [
  /Maximum call stack size exceeded/,
];

function isThirdParty(stack: string, source: string): boolean {
  const haystack = stack + source;
  return THIRD_PARTY_PATTERNS.some((re) => re.test(haystack));
}

function isUnattributable(message: string, source: string): boolean {
  // source starts with "undefined:" when filename is missing — third-party inline/eval script
  if (!source.startsWith('undefined:')) return false;
  return UNATTRIBUTABLE_MESSAGES.some((re) => re.test(message));
}

function isBenign(message: string): boolean {
  return BENIGN_MESSAGES.some((re) => re.test(message));
}

export default function CrashReporter() {
  useEffect(() => {
    function onError(event: ErrorEvent) {
      const source = `${event.filename}:${event.lineno}:${event.colno}`;
      const stack = event.error?.stack ?? '';
      if (isThirdParty(stack, source)) return;
      if (isBenign(event.message)) return;
      if (isUnattributable(event.message, source)) return;
      if (CROSS_ORIGIN_MESSAGE.test(event.message)) return;
      reportCrash({
        type: 'JS runtime error',
        message: event.message,
        stack,
        source,
      });
    }

    function onUnhandledRejection(event: PromiseRejectionEvent) {
      const reason = event.reason;
      const stack = reason?.stack ?? '';
      const message = String(reason?.message ?? reason ?? 'Unknown rejection');
      if (isThirdParty(stack, '')) return;
      if (isBenign(message)) return;
      reportCrash({
        type: 'Unhandled promise rejection',
        message,
        stack,
        source: 'unhandledrejection',
      });
    }

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return null;
}
